import L from 'leaflet';
import 'leaflet-routing-machine';

// Waypoint and callback types
type Waypoint = {
    latLng: L.LatLng;
    name?: string;
    options?: any;
};
type RouteCallback = (err: any, routes?: any[]) => void;

function registerMapboxRouting() {
    // Check if L.Routing exists
    if (!L.Routing) {
        console.warn('[leafletMapbox] L.Routing not available. leaflet-routing-machine may not have loaded yet.');
        return;
    }

    if ((L.Routing as any).MapboxRouting) {
        return;
    }

    (L.Routing as any).MapboxRouting = (L.Class as any).extend({
        options: {
            apiEndpoint: '/api/mapbox-route',
            timeout: 30_000,
            key: '',
        },

        initialize(options?: any) {
            L.Util.setOptions(this, options);
        },

        async route(waypoints: Waypoint[], callback: RouteCallback, context?: any, options?: any) {
            options = options || {};
            const { apiEndpoint, key, folder } = this.options;

            let timedOut = false;
            const timer = setTimeout(() => {
                timedOut = true;
                callback.call(context || callback, {
                    status: -1,
                    message: 'Route API request timed out.',
                });
            }, this.options.timeout);

            try {
                const points = waypoints.map((wp) => ({
                    lat: wp.latLng.lat,
                    lng: wp.latLng.lng,
                }));

                // Validation: key and folder required
                if (!key || !folder) {
                    clearTimeout(timer);
                    callback.call(context || callback, {
                        status: 400,
                        message: 'Missing `key` or `folder` parameter.',
                    });
                    return;
                }

                const resp = await fetch(apiEndpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ key, folder, pointA: points[0], pointB: points[1] }),
                });

                clearTimeout(timer);
                if (timedOut) return;

                if (!resp.ok) {
                    callback.call(context || callback, {
                        status: resp.status,
                        message: 'Route API request failed: ' + resp.statusText,
                    });
                    return;
                }

                const data = await resp.json();
                this._routeDone(data, waypoints, callback, context);
            } catch (err) {
                clearTimeout(timer);
                if (!timedOut) {
                    callback.call(context || callback, {
                        status: -1,
                        message: 'Route API request failed: ' + err,
                    });
                }
            }
            return this;
        },

        _routeDone(response: any, inputWaypoints: Waypoint[], callback: RouteCallback, context?: any) {
            context = context || callback;

            if (!response || !response.coordinates) {
                callback.call(context, {
                    status: response?.status || 'Error',
                    message: response?.message || 'Route API error',
                });
                return;
            }

            // Build Leaflet latlngs from coordinates [lng, lat]
            const latlngs = response.coordinates.map((c: [number, number]) => L.latLng(c[1], c[0]));

            const alts = [
                {
                    name: 'Route 0',
                    coordinates: latlngs,
                    instructions: [],
                    summary: response.summary ?? {},
                    inputWaypoints,
                    waypoints: [latlngs[0], latlngs[latlngs.length - 1]],
                },
            ];

            callback.call(context, null, alts);
        },
    });

    // Factory for convenient instantiation
    (L.Routing as any).mapboxRouting = function (options?: any) {
        return new (L.Routing as any).MapboxRouting(options);
    };
}

// Register routing class at client startup
// enforce: 'post' ensures this plugin runs after components are imported, allowing leaflet-routing-machine to be loaded first
export default defineNuxtPlugin({
    name: 'leaflet-mapbox-routing',
    enforce: 'post',
    setup() {
        if (!import.meta.client) return;

        // After import, L.Routing should be available synchronously
        if (!L.Routing) {
            console.error(
                '[leafletMapbox] L.Routing not available. Ensure leaflet-routing-machine is properly imported.'
            );
            return;
        }

        registerMapboxRouting();
    },
});
